import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/site/SEO";
import CTASection from "@/components/site/CTASection";
import { PROJECTS } from "@/lib/site";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  const [lightbox, setLightbox] = useState(null);

  if (!project) return <Navigate to="/projects" replace />;

  const related = PROJECTS.filter((p) => p.slug !== project.slug).slice(0, 3);
  const gallery = project.gallery || [project.image];

  return (
    <>
      <SEO
        title={`${project.title} — Projects — L F Building`}
        description={project.description || `A ${project.tag} project by L F Building & Maintenance Ltd in ${project.location}.`}
        path={`/projects/${project.slug}`}
      />

      {/* HERO */}
      <section className="bg-brand-ink text-white pt-28 md:pt-40 pb-16 md:pb-24 border-b border-white/10">
        <div className="container-lf max-w-5xl space-y-6">
          <Link to="/projects" data-testid="project-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-brand transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to projects
          </Link>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-white/60 flex-wrap">
            <span className="text-brand font-semibold">{project.tag}</span>
            <span>·</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {project.location}</span>
            <span>·</span>
            <span>{gallery.length} photo{gallery.length > 1 ? "s" : ""}</span>
          </div>
          <h1 className="font-display font-bold uppercase text-5xl md:text-7xl leading-[0.9]">{project.title}</h1>
          {project.description && (
            <p className="text-lg text-white/70 max-w-3xl">{project.description}</p>
          )}
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20 md:py-28 bg-white" data-testid="project-gallery">
        <div className="container-lf">
          <div className="mb-10 flex items-end justify-between">
            <h2 className="font-display font-bold uppercase text-3xl md:text-4xl">Gallery</h2>
            <span className="text-xs uppercase tracking-widest text-neutral-500">Tap any image to enlarge</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {gallery.map((src, i) => (
              <button
                key={i}
                onClick={() => setLightbox(src)}
                data-testid={`gallery-thumb-${i}`}
                className={`relative overflow-hidden border border-black/10 hover:border-brand transition-colors bg-neutral-100 group ${
                  i === 0 ? "md:col-span-2 aspect-[16/9]" : "aspect-[4/3]"
                }`}
              >
                <img src={src} alt={`${project.title} ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-brand-ink/0 group-hover:bg-brand-ink/10 transition-colors" />
                <div className="absolute bottom-3 left-3 bg-white text-brand-ink px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest">
                  0{i + 1} / 0{gallery.length}
                </div>
              </button>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-black/10">
            <p className="text-sm text-neutral-600 max-w-md">
              Interested in a similar project? Send us the details and we&apos;ll come back with a free, no-obligation quote.
            </p>
            <Link to="/contact">
              <Button className="rounded-none bg-brand hover:bg-brand-hover text-white h-12 px-6 uppercase tracking-wider text-sm font-semibold">
                Enquire About A Project Like This <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-16 md:py-24 bg-brand-alt border-y border-black/10" data-testid="project-related">
        <div className="container-lf">
          <h2 className="font-display font-bold uppercase text-3xl md:text-4xl mb-10">More projects</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link key={p.slug} to={`/projects/${p.slug}`} className="group border border-black/10 hover:border-brand transition-colors bg-white overflow-hidden">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="text-[11px] uppercase tracking-widest text-brand font-semibold mb-2">{p.tag}</div>
                  <h3 className="font-display font-bold uppercase text-xl leading-tight group-hover:text-brand transition-colors">{p.title}</h3>
                  <p className="text-sm text-neutral-500 mt-1">{p.location}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection eyebrow="Have a project in mind?" title="Let's price it up." />

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          role="dialog"
          aria-label="Image preview"
          data-testid="project-lightbox"
          className="fixed inset-0 z-[100] bg-brand-ink/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-fade-up"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            aria-label="Close preview"
            className="absolute top-4 right-4 w-12 h-12 grid place-items-center bg-white text-brand-ink hover:bg-brand hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={lightbox}
            alt={project.title}
            className="max-h-[90vh] max-w-[95vw] object-contain border border-white/10"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
